import { Location } from 'history'
import React from 'react'
import { ThunkDispatch } from 'redux-thunk'
import { ExpenseReportsFiltersBody } from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import { OrganizationConnectedPlatform } from 'src/enums'
import { UseGlobalDebouncedDispatcherReturn } from 'src/globalDebouncedDispatcher'
import { APIKey } from 'src/models/apiKey.models'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { OrgExpenseReportConfirmDateChangePopupProps } from 'src/models/orgExpenseReports.models'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserListModel } from 'src/models/user.models'
import { LocationState, RootState } from 'src/redux/createRootReducer'

export const RESET_APP_STATE = 'app/RESET_APP_STATE'
export interface ResetAppStateAction {
  type: typeof RESET_APP_STATE
}
export const createResetAppState = (): ResetAppStateAction => ({
  type: RESET_APP_STATE,
})

export const SET_IS_SIDENAV_OPEN = 'app/SET_IS_SIDENAV_OPEN'
export interface SetIsSidenavOpenAction {
  type: typeof SET_IS_SIDENAV_OPEN
  payload: {
    isSidenavOpen: boolean
  }
}
export const createSetIsSidenavOpen = (
  isSidenavOpen: boolean,
): SetIsSidenavOpenAction => ({
  type: SET_IS_SIDENAV_OPEN,
  payload: {
    isSidenavOpen,
  },
})

export const SET_IS_SESSION_EXPIRING_POPUP_OPEN =
  'app/SET_IS_SESSION_EXPIRING_POPUP_OPEN'
export interface SetIsSessionExpiringPopupOpenAction {
  type: typeof SET_IS_SESSION_EXPIRING_POPUP_OPEN
  payload: {
    isSessionExpiringPopupOpen: boolean
  }
}
export const createSetIsSessionExpiringPopupOpen = (
  isSessionExpiringPopupOpen: boolean,
): SetIsSessionExpiringPopupOpenAction => ({
  type: SET_IS_SESSION_EXPIRING_POPUP_OPEN,
  payload: {
    isSessionExpiringPopupOpen,
  },
})

export const SET_IS_TOOLTIP_POPUP_OPEN = 'app/SET_IS_TOOLTIP_POPUP_OPEN'
export interface SetIsTooltipPopupOpenAction {
  type: typeof SET_IS_TOOLTIP_POPUP_OPEN
  payload: {
    isTooltipPopupOpen: boolean
    tooltipPopupMessage?: React.ReactNode
  }
}
export const createSetIsTooltipPopupOpen = (
  isTooltipPopupOpen: boolean,
  tooltipPopupMessage?: React.ReactNode,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsTooltipPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_TOOLTIP_POPUP_OPEN,
      payload: {
        isTooltipPopupOpen,
        tooltipPopupMessage,
      },
    })
  }
}

export const SET_IS_ATTACHMENT_POPUP_OPEN = 'app/SET_IS_ATTACHMENT_POPUP_OPEN'
export interface SetIsAttachmentPopupOpenAction {
  type: typeof SET_IS_ATTACHMENT_POPUP_OPEN
  payload: {
    isAttachmentPopupOpen: boolean
    deleteAction?: () => void
    attachment?: ReceiptAttachmentModel
  }
}
export const createSetIsAttachmentPopupOpen = (
  isAttachmentPopupOpen: boolean,
  deleteAction?: () => void,
  attachment?: ReceiptAttachmentModel,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsAttachmentPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_ATTACHMENT_POPUP_OPEN,
      payload: {
        isAttachmentPopupOpen,
        deleteAction,
        attachment,
      },
    })
  }
}

export const SET_IS_DELETE_ACCOUNT_POPUP_OPEN =
  'app/SET_IS_DELETE_ACCOUNT_POPUP_OPEN'
export interface SetIsDeleteAccountPopupOpenAction {
  type: typeof SET_IS_DELETE_ACCOUNT_POPUP_OPEN
  payload: {
    isDeactivateBudgetPopupOpen: boolean
  }
}
export const createSetIsDeleteAccountPopupOpen = (
  isDeactivateBudgetPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsDeleteAccountPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_DELETE_ACCOUNT_POPUP_OPEN,
      payload: {
        isDeactivateBudgetPopupOpen,
      },
    })
  }
}

export const SET_IS_CANCEL_ORG_INVITE_POPUP_OPEN =
  'app/SET_IS_CANCEL_ORG_INVITE_POPUP_OPEN'
export interface SetIsCancelOrgInvitePopupOpenAction {
  type: typeof SET_IS_CANCEL_ORG_INVITE_POPUP_OPEN
  payload: {
    isCancelOrgInvitePopupOpen: boolean
  }
}
export const createSetIsCancelOrgInvitePopupOpen = (
  isCancelOrgInvitePopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsCancelOrgInvitePopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_CANCEL_ORG_INVITE_POPUP_OPEN,
      payload: {
        isCancelOrgInvitePopupOpen,
      },
    })
  }
}

export const SET_IS_CREATE_ORG_INTEGRATION_POPUP_OPEN =
  'app/SET_IS_CREATE_ORG_INTEGRATION_POPUP_OPEN'
export interface SetIsCreateOrgIntegrationOpenAction {
  type: typeof SET_IS_CREATE_ORG_INTEGRATION_POPUP_OPEN
  payload: {
    isCreateOrgIntegrationOpen: boolean
    integrationType?: OrganizationConnectedPlatform
  }
}
export const createSetIsCreateOrgIntegrationOpen = (
  isCreateOrgIntegrationOpen: boolean,
  integrationType?: OrganizationConnectedPlatform,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsCreateOrgIntegrationOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_CREATE_ORG_INTEGRATION_POPUP_OPEN,
      payload: {
        isCreateOrgIntegrationOpen,
        integrationType,
      },
    })
  }
}

export const SET_IS_MANAGE_ORG_INTEGRATION_POPUP_OPEN =
  'app/SET_IS_MANAGE_ORG_INTEGRATION_POPUP_OPEN'
export interface SetIsManageOrgIntegrationPopupOpenAction {
  type: typeof SET_IS_MANAGE_ORG_INTEGRATION_POPUP_OPEN
  payload: {
    isManageOrgIntegrationPopupOpen: boolean
  }
}
export const createSetIsManageOrgIntegrationPopupOpen = (
  isManageOrgIntegrationPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsManageOrgIntegrationPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_MANAGE_ORG_INTEGRATION_POPUP_OPEN,
      payload: {
        isManageOrgIntegrationPopupOpen,
      },
    })
  }
}

export const SET_IS_ORG_OWNER_REASSIGNMENT_POPUP_OPEN =
  'app/SET_IS_ORG_OWNER_REASSIGNMENT_POPUP_OPEN'
export interface SetIsOrgOwnerReassignmentPopupOpenAction {
  type: typeof SET_IS_ORG_OWNER_REASSIGNMENT_POPUP_OPEN
  payload: {
    isOrgOwnerReassignmentPopupOpen: boolean
  }
}
export const createSetIsOrgOwnerReassignmentPopupOpen = (
  isOrgOwnerReassignmentPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsOrgOwnerReassignmentPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_ORG_OWNER_REASSIGNMENT_POPUP_OPEN,
      payload: {
        isOrgOwnerReassignmentPopupOpen,
      },
    })
  }
}

export const SET_IS_BMO_WELCOME_POPUP_OPEN = 'app/SET_IS_BMO_WELCOME_POPUP_OPEN'
export interface SetIsBmoWelcomePopupOpenAction {
  type: typeof SET_IS_BMO_WELCOME_POPUP_OPEN
  payload: {
    isBmoWelcomePopupOpen: boolean
  }
}
export const createSetIsBmoWelcomePopupOpen = (
  isBmoWelcomePopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsBmoWelcomePopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_BMO_WELCOME_POPUP_OPEN,
      payload: {
        isBmoWelcomePopupOpen,
      },
    })
  }
}

export const SET_IS_BRAND_SPECIFIC_WELCOME_POPUP_OPEN =
  'app/SET_IS_BRAND_SPECIFIC_WELCOME_POPUP_OPEN'
export interface SetIsBrandSpecificWelcomePopupOpenAction {
  type: typeof SET_IS_BRAND_SPECIFIC_WELCOME_POPUP_OPEN
  payload: {
    isBrandSpecificWelcomePopupOpen: boolean
  }
}
export const createSetIsBrandSpecificWelcomePopupOpen = (
  isBrandSpecificWelcomePopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsBrandSpecificWelcomePopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_BRAND_SPECIFIC_WELCOME_POPUP_OPEN,
      payload: {
        isBrandSpecificWelcomePopupOpen,
      },
    })
  }
}

export const SET_IS_SEND_RECEIPT_REMINDER_POPUP_OPEN =
  'app/SET_IS_SEND_RECEIPT_REMINDER_POPUP_OPEN'
export interface SetIsSendReceiptReminderPopupOpenAction {
  type: typeof SET_IS_SEND_RECEIPT_REMINDER_POPUP_OPEN
  payload: {
    isSendReceiptReminderPopupOpen: boolean
  }
}
export const createSetIsSendReceiptReminderPopupOpen = (
  isSendReceiptReminderPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsSendReceiptReminderPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_SEND_RECEIPT_REMINDER_POPUP_OPEN,
      payload: {
        isSendReceiptReminderPopupOpen,
      },
    })
  }
}

export const SET_IS_ADD_DEPT_POPUP_OPEN = 'app/SET_IS_ADD_DEPT_POPUP_OPEN'
export interface SetIsAddDeptPopupOpenAction {
  type: typeof SET_IS_ADD_DEPT_POPUP_OPEN
  payload: {
    isAddDeptPopupOpen: boolean
  }
}

export const createSetIsAddDeptPopupOpen = (isAddDeptPopupOpen: boolean) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsAddDeptPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_ADD_DEPT_POPUP_OPEN,
      payload: {
        isAddDeptPopupOpen,
      },
    })
  }
}

export const SET_IS_API_KEY_CREATED_POPUP_OPEN =
  'app/SET_IS_ADD_API_KEY_POPUP_OPEN'
export interface SetIsAddAPIKeyPopupOpenAction {
  type: typeof SET_IS_API_KEY_CREATED_POPUP_OPEN
  payload: {
    isAPIKeyCreatedPopupOpen: boolean
    apiKey?: APIKey
  }
}
export const createSetIsAPIKeyCreatedPopupOpen = (
  isAPIKeyCreatedPopupOpen: boolean,
  apiKey: APIKey | undefined,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsAddAPIKeyPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_API_KEY_CREATED_POPUP_OPEN,
      payload: {
        isAPIKeyCreatedPopupOpen,
        apiKey,
      },
    })
  }
}

export const SET_IS_DELETE_API_KEY_POPUP_OPEN =
  'app/SET_IS_DELETE_API_KEY_POPUP_OPEN'
export interface SetIsDeleteAPIKeyPopupOpenAction {
  type: typeof SET_IS_DELETE_API_KEY_POPUP_OPEN
  payload: {
    isDeleteAPIKeyPopupOpen: boolean
    apiKeyId?: string
    expiry?: string
  }
}
export const createSetIsDeleteAPIKeyPopupOpen = (
  isDeleteAPIKeyPopupOpen: boolean,
  apiKeyId?: string,
  expiry?: string,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsDeleteAPIKeyPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_DELETE_API_KEY_POPUP_OPEN,
      payload: {
        isDeleteAPIKeyPopupOpen,
        apiKeyId,
        expiry,
      },
    })
  }
}

export const SET_IS_CREATE_EXPENSE_REPORT_POPUP_OPEN =
  'app/SET_IS_CREATE_EXPENSE_REPORT_POPUP_OPEN'
export interface SetIsCreateExpenseReportPopupOpenAction {
  type: typeof SET_IS_CREATE_EXPENSE_REPORT_POPUP_OPEN
  payload: {
    isCreateExpenseReportPopupOpen: boolean
  }
}
export const createSetIsCreateExpenseReportPopupOpen = (
  isCreateExpenseReportPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsCreateExpenseReportPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_CREATE_EXPENSE_REPORT_POPUP_OPEN,
      payload: {
        isCreateExpenseReportPopupOpen,
      },
    })
  }
}

export const SET_IS_MARK_EXPENSE_REPORTS_PAID_POPUP_OPEN =
  'app/SET_IS_MARK_EXPENSE_REPORTS_PAID_POPUP_OPEN'
export interface SetIsMarkExpenseReportsPaidPopupOpenAction {
  type: typeof SET_IS_MARK_EXPENSE_REPORTS_PAID_POPUP_OPEN
  payload: {
    isMarkExpenseReportsPaidPopupOpen: boolean
    expensesId?: string[]
    totalAmount?: number
  }
}
export const createSetIsMarkExpenseReportsPaidPopupOpen = (
  isMarkExpenseReportsPaidPopupOpen: boolean,
  expensesId?: string[],
  totalAmount?: number,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsMarkExpenseReportsPaidPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_MARK_EXPENSE_REPORTS_PAID_POPUP_OPEN,
      payload: {
        isMarkExpenseReportsPaidPopupOpen,
        expensesId,
        totalAmount,
      },
    })
  }
}

export const SET_IS_DELETE_EXPENSE_POPUP_OPEN =
  'app/SET_IS_DELETE_EXPENSE_POPUP_OPEN'
export interface SetIsDeleteExpensePopupOpenAction {
  type: typeof SET_IS_DELETE_EXPENSE_POPUP_OPEN
  payload: {
    isDeleteExpensePopupOpen: boolean
    selectedExpenses?: string[]
  }
}
export const createSetIsDeleteExpensePopupOpen = (
  isDeleteExpensePopupOpen: boolean,
  selectedExpenses?: string[],
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsDeleteExpensePopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_DELETE_EXPENSE_POPUP_OPEN,
      payload: {
        isDeleteExpensePopupOpen,
        selectedExpenses,
      },
    })
  }
}

export const SET_IS_DELETE_EXPENSE_REPORTS_POPUP_OPEN =
  'app/SET_IS_DELETE_EXPENSE_REPORTS_POPUP_OPEN'
export interface SetIsDeleteExpenseReportsPopupOpenAction {
  type: typeof SET_IS_DELETE_EXPENSE_REPORTS_POPUP_OPEN
  payload: {
    isDeleteExpenseReportsPopupOpen: boolean
    selectedExpenseReports?: string[]
  }
}
export const createSetIsDeleteExpenseReportsPopupOpen = (
  isDeleteExpenseReportsPopupOpen: boolean,
  selectedExpenseReports?: string[],
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsDeleteExpenseReportsPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_DELETE_EXPENSE_REPORTS_POPUP_OPEN,
      payload: {
        isDeleteExpenseReportsPopupOpen,
        selectedExpenseReports,
      },
    })
  }
}

export const SET_IS_SUBMIT_EXPENSE_REPORT_POPUP_OPEN =
  'app/SET_IS_SUBMIT_EXPENSE_REPORT_POPUP_OPEN'
export interface SetIsSubmitExpenseReportPopupOpenAction {
  type: typeof SET_IS_SUBMIT_EXPENSE_REPORT_POPUP_OPEN
  payload: {
    isSubmitExpenseReportPopupOpen: boolean
    numberOfExpenses?: number
  }
}
export const createSetIsSubmitExpenseReportPopupOpen = (
  isSubmitExpenseReportPopupOpen: boolean,
  numberOfExpenses?: number,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsSubmitExpenseReportPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_SUBMIT_EXPENSE_REPORT_POPUP_OPEN,
      payload: {
        isSubmitExpenseReportPopupOpen,
        numberOfExpenses,
      },
    })
  }
}

export const SET_IS_ADD_MISSING_INFORMATION_EXPENSE_REPORT_POPUP_OPEN =
  'app/SET_IS_ADD_MISSING_INFORMATION_EXPENSE_REPORT_POPUP_OPEN'
export interface SetIsAddMissingInformationExpenseReportPopupOpenAction {
  type: typeof SET_IS_ADD_MISSING_INFORMATION_EXPENSE_REPORT_POPUP_OPEN
  payload: {
    isAddMissingInformationExpenseReportPopupOpen: boolean
  }
}
export const createSetIsAddMissingInformationExpenseReportPopupOpen = (
  isAddMissingInformationExpenseReportPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsAddMissingInformationExpenseReportPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_ADD_MISSING_INFORMATION_EXPENSE_REPORT_POPUP_OPEN,
      payload: {
        isAddMissingInformationExpenseReportPopupOpen,
      },
    })
  }
}

export const SET_IS_WHATS_NEW_POPUP_OPEN = 'app/SET_IS_WHATS_NEW_POPUP_OPEN'
export interface SetIsWhatsNewPopupOpenAction {
  type: typeof SET_IS_WHATS_NEW_POPUP_OPEN
  payload: {
    isWhatsNewPopupOpen: boolean
  }
}
export const createSetIsWhatsNewPopupOpen = (isWhatsNewPopupOpen: boolean) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsWhatsNewPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_WHATS_NEW_POPUP_OPEN,
      payload: {
        isWhatsNewPopupOpen,
      },
    })
  }
}

export const SET_IS_ACKNOWLEDGEMENT_ALERT_BAR_OPEN =
  'app/SET_IS_ACKNOWLEDGEMENT_ALERT_BAR_OPEN'
export interface SetIsAcknowledgementAlertBarOpenAction {
  type: typeof SET_IS_ACKNOWLEDGEMENT_ALERT_BAR_OPEN
  payload: {
    isAcknowledgementAlertBarOpen: boolean
  }
}
export const createSetIsAcknowledgementAlertBarOpen = (
  isAcknowledgementAlertBarOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsAcknowledgementAlertBarOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_ACKNOWLEDGEMENT_ALERT_BAR_OPEN,
      payload: {
        isAcknowledgementAlertBarOpen,
      },
    })
  }
}

export const SET_IS_SETUP_ORGANIZATION_POPUP_OPEN =
  'app/SET_IS_SETUP_ORGANIZATION_POPUP_OPEN'
export interface SetIsSetupOrganizationPopupOpenAction {
  type: typeof SET_IS_SETUP_ORGANIZATION_POPUP_OPEN
  payload: {
    isSetupOrganizationPopupOpen: boolean
    continueLocation?: string | Location<LocationState>
  }
}
export const createSetIsSetupOrganizationPopupOpen = (
  isSetupOrganizationPopupOpen: boolean,
  continueLocation?: string | Location<LocationState>,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsSetupOrganizationPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_SETUP_ORGANIZATION_POPUP_OPEN,
      payload: {
        isSetupOrganizationPopupOpen,
        continueLocation,
      },
    })
  }
}

export const SET_IS_DOWNLOAD_SUCCESS_POPUP_OPEN =
  'app/SET_IS_DOWNLOAD_SUCCESS_POPUP_OPEN'
export interface SetIsDownloadSuccessPopupOpenAction {
  type: typeof SET_IS_DOWNLOAD_SUCCESS_POPUP_OPEN
  payload: {
    isDownloadSuccessPopupOpen: boolean
  }
}
export const createSetIsDownloadSuccessPopupOpen = (
  isDownloadSuccessPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsDownloadSuccessPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_DOWNLOAD_SUCCESS_POPUP_OPEN,
      payload: {
        isDownloadSuccessPopupOpen,
      },
    })
  }
}

export const SET_IS_DOWNLOAD_EXPENSE_REPORTS_PAID_POPUP_OPEN =
  'app/SET_IS_DOWNLOAD_EXPENSE_REPORTS_PAID_POPUP_OPEN'
export interface SetIsDownloadExpenseReportsPaidPopupOpenAction {
  type: typeof SET_IS_DOWNLOAD_EXPENSE_REPORTS_PAID_POPUP_OPEN
  payload: {
    isDownloadExpenseReportsPaidPopupOpen: boolean
    filtersBody?: ExpenseReportsFiltersBody
  }
}
export const createSetIsDownloadExpenseReportsPaidPopupOpen = (
  isDownloadExpenseReportsPaidPopupOpen: boolean,
  filtersBody?: ExpenseReportsFiltersBody,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsDownloadExpenseReportsPaidPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_DOWNLOAD_EXPENSE_REPORTS_PAID_POPUP_OPEN,
      payload: {
        isDownloadExpenseReportsPaidPopupOpen,
        filtersBody,
      },
    })
  }
}

export const SET_IS_SYNC_TRANSACTIONS_POPUP_OPEN =
  'app/SET_IS_SYNC_TRANSACTIONS_POPUP_OPEN'
export interface SetIsSyncTransactionsPopupOpenAction {
  type: typeof SET_IS_SYNC_TRANSACTIONS_POPUP_OPEN
  payload: {
    isSyncTransactionsPopupOpen: boolean
    selectedTransactions?: string[]
  }
}
export const createSetIsSyncTransactionsPopupOpen = (
  isSyncTransactionsPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsSyncTransactionsPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_SYNC_TRANSACTIONS_POPUP_OPEN,
      payload: {
        isSyncTransactionsPopupOpen,
      },
    })
  }
}

export const SETUP_GLOBAL_DISPATCHER = 'app/SETUP_GLOBAL_DISPATCHER'
export interface SetupGlobalDispatcherAction {
  type: typeof SETUP_GLOBAL_DISPATCHER
  payload: {
    globalDispatchers: UseGlobalDebouncedDispatcherReturn
  }
}
export const createSetupGlobalDispatcher = (
  globalDispatchers: UseGlobalDebouncedDispatcherReturn,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetupGlobalDispatcherAction>,
  ): void => {
    dispatch({
      type: SETUP_GLOBAL_DISPATCHER,
      payload: {
        globalDispatchers,
      },
    })
  }
}

export const SET_IS_UPLOAD_RECEIPTS_AUTO_MATCH_POPUP_OPEN =
  'app/SET_IS_UPLOAD_RECEIPTS_AUTO_MATCH_POPUP_OPEN'
export interface SetIsUploadReceiptsAutoMatchPopupOpenAction {
  type: typeof SET_IS_UPLOAD_RECEIPTS_AUTO_MATCH_POPUP_OPEN
  payload: {
    isUploadReceiptsAutoMatchPopupOpen: boolean
  }
}
export const createSetIsUploadReceiptsAutoMatchPopupOpen = (
  isUploadReceiptsAutoMatchPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsUploadReceiptsAutoMatchPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_UPLOAD_RECEIPTS_AUTO_MATCH_POPUP_OPEN,
      payload: {
        isUploadReceiptsAutoMatchPopupOpen,
      },
    })
  }
}

export const SET_IS_REMOVE_BUDGET_USER_POPUP_OPEN =
  'app/SET_IS_REMOVE_BUDGET_USER_POPUP_OPEN'
export interface SetIsRemoveBudgetUserPopupOpenAction {
  type: typeof SET_IS_REMOVE_BUDGET_USER_POPUP_OPEN
  payload: {
    isRemoveBudgetUserPopupOpen: boolean
    budget?: CreditCardListModel
    budgetUser?: UserListModel
  }
}
export const createSetIsRemoveBudgetUserInvitePopupOpen = (
  isRemoveBudgetUserPopupOpen: boolean,
  budget?: CreditCardListModel,
  budgetUser?: UserListModel,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsRemoveBudgetUserPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_REMOVE_BUDGET_USER_POPUP_OPEN,
      payload: {
        isRemoveBudgetUserPopupOpen,
        budget,
        budgetUser,
      },
    })
  }
}

export const SET_IS_MOBILE_WEB_POPUP_OPEN = 'app/SET_IS_MOBILE_WEB_POPUP_OPEN'
export interface SetIsMobileWebPopupOpenAction {
  type: typeof SET_IS_MOBILE_WEB_POPUP_OPEN
  payload: {
    isMobileWebPopupOpen: boolean
  }
}
export const createSetIsMobileWebPopupOpen = (
  isMobileWebPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsMobileWebPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_MOBILE_WEB_POPUP_OPEN,
      payload: {
        isMobileWebPopupOpen,
      },
    })
  }
}

export const SET_IS_REQUEST_COMPLETED_POPUP_OPEN =
  'app/SET_IS_REQUEST_COMPLETED_POPUP_OPEN'
export interface SetIsRequestCompletedPopupOpenAction {
  type: typeof SET_IS_REQUEST_COMPLETED_POPUP_OPEN
  payload: {
    isRequestCompletedPopupOpen: boolean
  }
}
export const createSetIsRequestCompletedPopupOpen = (
  isRequestCompletedPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsRequestCompletedPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_REQUEST_COMPLETED_POPUP_OPEN,
      payload: {
        isRequestCompletedPopupOpen,
      },
    })
  }
}

export const SET_SHOW_SIDENAV_LARGE = 'app/SET_SHOW_SIDENAV_LARGE'
export interface SetShowSidenavLargeAction {
  type: typeof SET_SHOW_SIDENAV_LARGE
  payload: {
    showSidenavLarge: boolean
  }
}

export const createSetShowSidenavLarge = (showSidenavLarge: boolean) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetShowSidenavLargeAction>,
  ): void => {
    dispatch({
      type: SET_SHOW_SIDENAV_LARGE,
      payload: {
        showSidenavLarge,
      },
    })
  }
}

export const SET_IS_SIDENAV_LOCKED_OPEN = 'app/SET_IS_SIDENAV_LOCKED_OPEN'
export interface SetIsSidenavLockedOpenAction {
  type: typeof SET_IS_SIDENAV_LOCKED_OPEN
  payload: {
    isSidenavLockedOpen: boolean
  }
}

export const createSetShowSidenavLockedOpen = (
  isSidenavLockedOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsSidenavLockedOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_SIDENAV_LOCKED_OPEN,
      payload: {
        isSidenavLockedOpen,
      },
    })
  }
}

export const SET_IS_BULK_DEACTIVATE_VIRTUAL_CARDS_POPUP_OPEN =
  'app/SET_IS_BULK_DEACTIVATE_VIRTUAL_CARDS_POPUP_OPEN'
export interface SetIsBulkDeactivateVirtualCardsPopupOpenAction {
  type: typeof SET_IS_BULK_DEACTIVATE_VIRTUAL_CARDS_POPUP_OPEN
  payload: {
    isBulkDeactivateVirtualCardsPopupOpen: boolean
  }
}
export const createSetIsBulkDeactivateVirtualCardsPopupOpen = (
  isBulkDeactivateVirtualCardsPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsBulkDeactivateVirtualCardsPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_BULK_DEACTIVATE_VIRTUAL_CARDS_POPUP_OPEN,
      payload: {
        isBulkDeactivateVirtualCardsPopupOpen,
      },
    })
  }
}

export const SET_IS_BULK_UPDATE_VIRTUAL_CARDS_FLYOUT_OPEN =
  'app/SET_IS_BULK_UPDATE_VIRTUAL_CARDS_FLYOUT_OPEN'
export interface SetIsBulkUpdateVirtualCardsFlyoutOpenAction {
  type: typeof SET_IS_BULK_UPDATE_VIRTUAL_CARDS_FLYOUT_OPEN
  payload: {
    isBulkUpdateVirtualCardsFlyoutOpen: boolean
  }
}
export const createSetIsBulkUpdateVirtualCardsFlyoutOpen = (
  isBulkUpdateVirtualCardsFlyoutOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsBulkUpdateVirtualCardsFlyoutOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_BULK_UPDATE_VIRTUAL_CARDS_FLYOUT_OPEN,
      payload: {
        isBulkUpdateVirtualCardsFlyoutOpen,
      },
    })
  }
}

export const SET_IS_BULK_CLOSE_VIRTUAL_CARDS_POPUP_OPEN =
  'app/SET_IS_BULK_CLOSE_VIRTUAL_CARDS_POPUP_OPEN'

export interface SetIsBulkCloseVirtualCardsPopupOpenAction {
  type: typeof SET_IS_BULK_CLOSE_VIRTUAL_CARDS_POPUP_OPEN
  payload: {
    isBulkCloseVirtualCardsPopupOpen: boolean
  }
}

export const createSetIsBulkCloseVirtualCardsPopupOpen = (
  isBulkCloseVirtualCardsPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsBulkCloseVirtualCardsPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_BULK_CLOSE_VIRTUAL_CARDS_POPUP_OPEN,
      payload: {
        isBulkCloseVirtualCardsPopupOpen,
      },
    })
  }
}

export const SET_IS_ADD_SECONDARY_EMAIL_POPUP_OPEN =
  'app/SET_IS_ADD_SECONDARY_EMAIL_POPUP_OPEN'
export interface SetIsAddSecondaryEmailPopupOpenAction {
  type: typeof SET_IS_ADD_SECONDARY_EMAIL_POPUP_OPEN
  payload: {
    isAddSecondaryEmailPopupOpen: boolean
    email?: string
  }
}
export const createSetIsAddSecondaryEmailPopupOpen = (
  isAddSecondaryEmailPopupOpen: boolean,
  email?: string,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsAddSecondaryEmailPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_ADD_SECONDARY_EMAIL_POPUP_OPEN,
      payload: {
        isAddSecondaryEmailPopupOpen,
        email,
      },
    })
  }
}

export const SET_IS_FILTERS_FLYOUT_OPEN = 'app/SET_IS_FILTERS_FLYOUT_OPEN'

export interface SetFiltersFlyoutOpenAction {
  type: typeof SET_IS_FILTERS_FLYOUT_OPEN
  payload: {
    isFiltersFlyoutOpen: boolean
  }
}

export const createSetFiltersFlyoutOpen = (isFiltersFlyoutOpen: boolean) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetFiltersFlyoutOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_FILTERS_FLYOUT_OPEN,
      payload: {
        isFiltersFlyoutOpen,
      },
    })
  }
}

export const SET_CONFIRM_ORG_EXPENSE_REPORT_DATE_CHANGE_POPUP =
  'app/SET_IS_CONFIRM_ORG_EXPENSE_REPORT_DATE_CHANGE_POPUP_OPEN'

export interface SetConfirmOrgExpenseReportDateChangePopupAction {
  type: typeof SET_CONFIRM_ORG_EXPENSE_REPORT_DATE_CHANGE_POPUP
  payload: {
    confirmOrgExpenseReportDateChangePopupProps:
      | OrgExpenseReportConfirmDateChangePopupProps
      | undefined
  }
}

export const createSetConfirmOrgExpenseReportDateChangePopup = (
  confirmOrgExpenseReportDateChangePopupProps:
    | OrgExpenseReportConfirmDateChangePopupProps
    | undefined,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetConfirmOrgExpenseReportDateChangePopupAction
    >,
  ): void => {
    dispatch({
      type: SET_CONFIRM_ORG_EXPENSE_REPORT_DATE_CHANGE_POPUP,
      payload: {
        confirmOrgExpenseReportDateChangePopupProps,
      },
    })
  }
}

export const SET_IS_EXTRA_VERIFICATION_POPUP_OPEN =
  'app/SET_IS_EXTRA_VERIFICATION_POPUP_OPEN'

export interface SetIsExtraVerificationPopupOpenAction {
  type: typeof SET_IS_EXTRA_VERIFICATION_POPUP_OPEN
  payload: {
    isExtraVerificationPopupOpen: boolean
  }
}

export const createSetIsExtraVerificationPopupOpen = (
  isExtraVerificationPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsExtraVerificationPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_EXTRA_VERIFICATION_POPUP_OPEN,
      payload: {
        isExtraVerificationPopupOpen,
      },
    })
  }
}

export const SET_IS_CHOOSE_2FA_METHOD_POPUP_OPEN =
  'app/SET_IS_CHOOSE_2FA_METHOD_POPUP_OPEN'
export interface SetIsChoose2faMethodPopupOpenAction {
  type: typeof SET_IS_CHOOSE_2FA_METHOD_POPUP_OPEN
  payload: {
    isChoose2faMethodPopupOpen: boolean
  }
}
export const createSetIsChoose2faMethodPopupOpen = (
  isChoose2faMethodPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      SetIsChoose2faMethodPopupOpenAction
    >,
  ): void => {
    dispatch({
      type: SET_IS_CHOOSE_2FA_METHOD_POPUP_OPEN,
      payload: {
        isChoose2faMethodPopupOpen,
      },
    })
  }
}

export const SET_IS_BRAND_REDIRECT_POPUP_OPEN =
  'app/SET_IS_BRAND_REDIRECT_POPUP_OPEN'
export interface SetIsBrandRedirectPopupOpenAction {
  type: typeof SET_IS_BRAND_REDIRECT_POPUP_OPEN
  payload: {
    isBrandRedirectPopupOpen: boolean
  }
}
export const createSetIsBrandRedirectPopupOpen = (
  isBrandRedirectPopupOpen: boolean,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, SetIsBrandRedirectPopupOpenAction>,
  ): void => {
    dispatch({
      type: SET_IS_BRAND_REDIRECT_POPUP_OPEN,
      payload: {
        isBrandRedirectPopupOpen,
      },
    })
  }
}
